import { Box } from '@mui/material';
import { format, Locale, parseISO, parse } from 'date-fns';
import { DataGridHandler } from '../DataGridFlexSales';
import StatusBullet from '../StatusBullet';

export interface SynchroLogItem {
  country: string;
  store: string;
  accountingDateFrom: string | Date;
  accountingDateTo: string | Date;
  justification: string;
  executionDate: string | Date;
  status: string;
  user: string;
  message?: string;
  sent?: string | null;
}

export interface SynchroDataGridContext {
  t: (key: string | string[]) => string;
}

const initialContext: SynchroDataGridContext = {
  t: () => '',
};

const safeRenderDate = (value: any, fmt = 'yyyy-MM-dd') => {
  if (!value) return '';

  let d: Date;
  if (typeof value === 'string') {
    if (value.length === 10) {
      d = parse(value, 'yyyy-MM-dd', new Date());
    } else {
      d = parseISO(value);
    }

    if (process.env.NODE_ENV === 'development' && fmt === 'P') {
      console.log(
        `%c🧮 [Grid Cell] Formateando: ${value} -> ${d.toDateString()}`,
        'color: #8b5cf6;'
      );
    }
  } else {
    d = value;
  }

  return !Number.isNaN(d.getTime()) ? format(d, fmt) : '';
};

export default (locale: Locale) => {
  const handler = new DataGridHandler<SynchroLogItem, SynchroDataGridContext>(
    initialContext,
    locale
  );

  handler.addColumn({
    accessor: 'country',
    type: 'string',
    title: ({ t }) => t('synchro4me.columns.country') || 'Country',
    width: { px: 100, pt: 0 },
  });

  handler.addColumn({
    accessor: 'store',
    type: 'string',
    title: ({ t }) => t('synchro4me.columns.store') || 'Store',
    description: ({ t }) => t('synchro4me.description.store'),
    width: { px: 150, pt: 0 },
  });

  handler.addColumn({
    accessor: 'accountingDateFrom',
    type: 'date',
    title: ({ t }) => t('synchro4me.columns.fromDateAccounting') || 'Date From',
    renderCell: val => safeRenderDate(val, 'P'),
  });

  handler.addColumn({
    accessor: 'accountingDateTo',
    type: 'date',
    title: ({ t }) => t('synchro4me.columns.toDateAccounting') || 'Date To',
    renderCell: val => safeRenderDate(val, 'P'),
  });

  handler.addColumn({
    accessor: 'justification',
    type: 'string',
    title: ({ t }) => t('synchro4me.columns.justification') || 'Justification',
    width: { px: 250, pt: 0 },
  });

  handler.addColumn({
    accessor: 'executionDate',
    type: 'dateTime',
    title: ({ t }) => t('synchro4me.columns.execution_date') || 'Execution Date',
    width: { px: 180, pt: 0 },
    renderCell: val => safeRenderDate(val, 'P kk:mm:ss'),
  });

  handler.addColumn({
    accessor: 'status',
    type: 'string',
    title: ({ t }) => t('synchro4me.columns.status') || 'Status',
    width: { px: 120, pt: 0 },
    renderCell: (_value, row) => <StatusBullet status={row.status} />,
  });

  handler.addColumn({
    accessor: 'user',
    type: 'string',
    title: ({ t }) => t('synchro4me.columns.user') || 'Executed By',
    width: { px: 150, pt: 0 },
  });

  handler.addColumn({
    accessor: 'message',
    type: 'string',
    title: ({ t }) => t('synchro4me.columns.message') || 'Message',
    width: { px: 200, pt: 0 },
  });

  handler.addColumn({
    accessor: 'sent',
    type: 'string',
    title: ({ t }) => t('synchro4me.columns.sent') || 'Sent',
    width: { px: 100, pt: 0 },
    renderCell: (val, _row, { t }) => {
      if (val === 'S') return t('synchro4me.labels.sent_status.yes');
      if (val === 'N') return t('synchro4me.labels.sent_status.no');
      return '';
    },
  });

  return handler;
};
